/** @file matchTreeSearch: the nodes a tree search shows (matches plus the ancestors that give them context). */
import type { Puppet, PuppetNode } from '@zilo/pose3d-format/types/puppet';
import { nodeSide } from './nodeSide';
import type { SearchTerm, TreeSearch } from './parseTreeSearch';

type Searched = Pick<Puppet, 'nodes' | 'sockets' | 'drawables'>;

function termMatches(puppet: Searched, name: string, node: PuppetNode, term: SearchTerm): boolean {
  switch (term.kind) {
    case 'text': return name.toLowerCase().includes(term.text);
    case 'group': return node.group === term.group;
    case 'side': return nodeSide(name) === term.side && name.toLowerCase().includes(term.text);
    case 'drawable': return puppet.drawables.some((d) => d.node === name && d.id.toLowerCase().includes(term.text));
    case 'socket': return Object.entries(puppet.sockets).some(([socket, n]) => n === name && socket.toLowerCase().includes(term.text));
  }
}

/** True when the node is in one of the chip `groups` (or no chip is on). */
const inChips = (node: PuppetNode, groups: ReadonlySet<number>): boolean =>
  groups.size === 0 || (node.group !== undefined && groups.has(node.group));

/**
 * Null when `terms` is empty and no chip `groups` is on (no filter). Otherwise the set of nodes that satisfy every
 * term (AND) and, when `groups` is non-empty, belong to one of those groups (chips are OR among themselves, AND with
 * the terms), together with all their ancestors.
 */
export function matchTreeSearch(puppet: Searched, terms: TreeSearch, groups: ReadonlySet<number>): Set<string> | null {
  if (terms.length === 0 && groups.size === 0) return null;
  const out = new Set<string>();
  for (const [name, node] of Object.entries(puppet.nodes)) {
    if (!inChips(node, groups) || !terms.every((t) => termMatches(puppet, name, node, t))) continue;
    for (let at: string | undefined = name; at !== undefined && !out.has(at); at = puppet.nodes[at]?.parent) out.add(at);
  }
  return out;
}
